import * as React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';

import {
    Typography, MenuItem, ListItemIcon, ListItemText, Divider, Avatar, Badge,
} from '@material-ui/core';
import { AssignmentInd } from '@material-ui/icons';
import { withRouter } from 'react-router';
import {
    LogoutIcon, HomeIcon, PersonIcon, ChatIcon, VideoChatIcon, FavouritesIcon, VideoIcon, CalendarIcon, PrintIcon, FeedbackIcon, OrgIcon,
} from '../icons';
import ElementIconOrDefault from '../icons/ElementIconOrDefault';

import { activityLabels, patientProgramAppElementSettings } from '../constants';

import urls from '../../app/urls';
import TrackedLink from './TrackedLink';
import { LanguageToggle } from '../../youwell-common/components';
import { hasValue } from '../../youwell-common/objectUtils';
import { loginLevels } from '../../youwell-common/constants';
import Auth from '../../youwell-common/Auth';
import { hasElementSetting } from '../taskUtils';
import { useYouwellDispatch, useYouwellSelector } from '../../app/store';
import * as AuthStore from '../../app/store/AuthStore';
import useYouwellTranslate from '../../app/localization/translate';


const useStyles = makeStyles(theme => ({
    profileAvatar: {
        marginLeft: -6,
        marginRight: theme.spacing(3),
        background: theme.palette.primary.main,
    },
    divider: {
        margin: theme.spacing(2, 0),
    },
    noProfilePadding: {
        paddingTop: theme.spacing(6),
    },
    version: {
        marginTop: theme.spacing(2),
    },
    menuItem: {
    },
    icon: {
        color: theme.palette.primary.main,
    },
    active: {
        borderLeft: theme.youwell.borders.secondary,
    },
}));

const MenuContent = ({ location }) => {
    const translate = useYouwellTranslate();
    const classes = useStyles();
    const dispatch = useYouwellDispatch();

    const unreadMessages = useYouwellSelector(state => state.user.unreadMessages);
    const currentProgram = useYouwellSelector(state => state.programStore.currentProgram);
    const clientConfig = useYouwellSelector(state => state.auth.clientConfig);
    const appVersion = useYouwellSelector(state => state.pageSettings.version);
    const profileData = useYouwellSelector(state => state.auth.profileData);

    const appItems = currentProgram?.appElements?.filter(d => !hasElementSetting(d, patientProgramAppElementSettings.hidden) && !d.disabled) || [];
    const hasCurrentProgram = hasValue(currentProgram);
    const hasActiveProgram = currentProgram?.isActiveProgram;
    const hasConsentTasks = profileData.patientConsents?.length > 0;

    const isActiveMenuElement = url => location.pathname === url;

    const logoutOrg = () => {
        dispatch(AuthStore.actionCreators.logoutOrg());
    };

    return (
        <>
            {(clientConfig.appSetup.features.profile && profileData) ? (
                <>
                    <MenuItem
                        className={classnames(classes.menuItem, { [classes.active]: isActiveMenuElement(urls.inside.profile) })}
                        component={TrackedLink}
                        to={urls.inside.profile}
                        label={activityLabels.menu.profile}
                    >
                        <Avatar className={classes.profileAvatar}>
                            <PersonIcon />
                        </Avatar>
                        <ListItemText primary={translate('menu.profile')} secondary={profileData.name} />
                    </MenuItem>

                    <Divider className={classes.divider} />
                </>
            ) : (
                <div className={classes.noProfilePadding} />
            )}


            <MenuItem
                className={classnames(classes.menuItem, { [classes.active]: isActiveMenuElement(urls.inside.home) })}
                component={TrackedLink}
                to={urls.inside.home}
                label={activityLabels.menu.home}
            >
                <ListItemIcon>
                    <HomeIcon className={classes.icon} />
                </ListItemIcon>
                <ListItemText primary={translate('menu.startPage')} />
            </MenuItem>

            {hasCurrentProgram && clientConfig.appSetup.features.favourites && (
                <MenuItem
                    className={classnames(classes.menuItem, { [classes.active]: isActiveMenuElement(urls.inside.pages.favourites) })}
                    component={TrackedLink}
                    to={urls.inside.pages.favourites}
                    label={activityLabels.menu.favourites}
                >
                    <ListItemIcon>
                        <FavouritesIcon className={classes.icon} />
                    </ListItemIcon>
                    <ListItemText primary={translate('header.favourites')} />
                </MenuItem>
            )}

            {hasCurrentProgram && clientConfig.appSetup.features.videoPage && (
                <MenuItem
                    className={classnames(classes.menuItem, { [classes.active]: isActiveMenuElement(urls.inside.pages.videos) })}
                    component={TrackedLink}
                    to={urls.inside.pages.videos}
                    label={activityLabels.menu.videos}
                >
                    <ListItemIcon>
                        <VideoIcon className={classes.icon} />
                    </ListItemIcon>
                    <ListItemText primary={translate('menu.videos')} />
                </MenuItem>
            )}

            {hasActiveProgram && clientConfig.appSetup.features.calendar && (
                <MenuItem
                    className={classnames(classes.menuItem, { [classes.active]: isActiveMenuElement(urls.inside.pages.calendar) })}
                    component={TrackedLink}
                    to={urls.inside.pages.calendar}
                    label={activityLabels.menu.calendar}
                >
                    <ListItemIcon>
                        <CalendarIcon className={classes.icon} />
                    </ListItemIcon>
                    <ListItemText primary={translate('menu.calendar')} />
                </MenuItem>
            )}

            {hasActiveProgram && clientConfig.appSetup.pages.filter(d => hasCurrentProgram || !d.hideIfNoProgram).map(item => (
                <MenuItem
                    className={classnames(classes.menuItem, { [classes.active]: isActiveMenuElement(item.url) })}
                    component={TrackedLink}
                    key={item.key}
                    to={item.url}
                    label={`${activityLabels.menu.page}/${item.key}`}
                >
                    <ListItemIcon><ElementIconOrDefault item={item} className={classes.icon} /></ListItemIcon>
                    <ListItemText
                        primary={translate(item.nameKey)}
                        className={classnames({ [classes.selected]: location.pathname?.includes(item.url) })}
                    />
                </MenuItem>
            ))}

            {appItems.map(item => (
                <MenuItem
                    className={classnames(classes.menuItem, { [classes.active]: isActiveMenuElement(`${urls.inside.program}/${item.elementGroup}/${item.indexInGroup}`) })}
                    component={TrackedLink}
                    key={item.id}
                    to={`${urls.inside.program}/${item.elementGroup}/${item.indexInGroup}`}
                    label={`${activityLabels.menu.appPage}/${item.name}`}
                >
                    <ListItemIcon><ElementIconOrDefault item={item} className={classes.icon} /></ListItemIcon>
                    <ListItemText
                        primary={item.name}
                        className={classnames({ [classes.selected]: location.pathname?.includes(`${urls.inside.program}/${item.elementGroup}/${item.indexInGroup}`) })}
                    />
                </MenuItem>
            ))}

            {hasActiveProgram && clientConfig.appSetup.features.chat && (
                <MenuItem
                    className={classnames(classes.menuItem, { [classes.active]: isActiveMenuElement(urls.inside.chat) })}
                    component={TrackedLink}
                    to={urls.inside.chat}
                    label={activityLabels.menu.chat}
                >
                    <ListItemIcon>
                        <Badge className={classes.badge} badgeContent={unreadMessages} color="secondary" overlap="rectangular">
                            <ChatIcon className={classes.icon} />
                        </Badge>
                    </ListItemIcon>
                    <ListItemText primary={translate('messages')} />
                </MenuItem>
            )}

            {hasActiveProgram && clientConfig.appSetup.features.videoChat && (
                <MenuItem
                    className={classnames(classes.menuItem, { [classes.active]: isActiveMenuElement(urls.inside.videochat) })}
                    component={TrackedLink}
                    to={urls.inside.videochat}
                    label={activityLabels.menu.videochat}
                >
                    <ListItemIcon>
                        <VideoChatIcon className={classes.icon} />
                    </ListItemIcon>
                    <ListItemText primary={translate('videochat')} />
                </MenuItem>
            )}

            {hasCurrentProgram && clientConfig.appSetup.features.printsPage && (
                <MenuItem
                    className={classnames(classes.menuItem, { [classes.active]: isActiveMenuElement(urls.inside.pages.prints) })}
                    component={TrackedLink}
                    to={urls.inside.pages.prints}
                    label={activityLabels.menu.prints}
                >
                    <ListItemIcon>
                        <PrintIcon className={classes.icon} />
                    </ListItemIcon>
                    <ListItemText primary={translate('menu.printPage')} />
                </MenuItem>
            )}

            {hasConsentTasks && (
                <MenuItem
                    className={classnames(classes.menuItem, { [classes.active]: isActiveMenuElement(urls.inside.pages.consent) })}
                    component={TrackedLink}
                    to={urls.inside.pages.consent}
                    label={activityLabels.menu.consent}
                >
                    <ListItemIcon>
                        <AssignmentInd className={classes.icon} />
                    </ListItemIcon>
                    <ListItemText primary={translate('menu.consent')} />
                </MenuItem>
            )}

            {clientConfig.appSetup.features.feedback && (
                <MenuItem
                    className={classnames(classes.menuItem, { [classes.active]: isActiveMenuElement(urls.inside.pages.feedback) })}
                    component={TrackedLink}
                    to={urls.inside.pages.feedback}
                    label={activityLabels.menu.feedback}
                >
                    <ListItemIcon>
                        <FeedbackIcon className={classes.icon} />
                    </ListItemIcon>
                    <ListItemText primary={translate('menu.feedback')} />
                </MenuItem>
            )}

            <Divider className={classes.divider} />

            {clientConfig.appSetup.features.org && profileData.connectedOrganizations?.length > 1 && (
                <MenuItem
                    className={classes.menuItem}
                    onClick={logoutOrg}
                    label={activityLabels.menu.org}
                >
                    <ListItemIcon>
                        <OrgIcon className={classes.icon} />
                    </ListItemIcon>
                    <ListItemText primary={profileData.organization?.name} />
                </MenuItem>
            )}

            {clientConfig.appSetup.features.logout && (
                <>
                    <MenuItem
                        className={classnames(classes.menuItem, { [classes.active]: isActiveMenuElement(urls.logout) })}
                        component={TrackedLink}
                        to={urls.logout}
                        label={activityLabels.menu.logout}
                    >
                        <ListItemIcon>
                            <LogoutIcon className={classes.icon} />
                        </ListItemIcon>
                        <ListItemText primary={Auth.getLoginLevel() > loginLevels.key ? translate('logout') : translate('menu.exit')} />
                    </MenuItem>

                    <Divider className={classes.divider} />
                </>
            )}

            {clientConfig.appSetup.features.languages && (
                <LanguageToggle />
            )}

            <div className={classes.version}>
                <Typography variant="caption" align="center" display="block">
                    {`ver. ${appVersion}`}
                </Typography>
            </div>
        </>
    );
};

MenuContent.propTypes = {
    location: PropTypes.object.isRequired,
};

const WithRef = withRouter(MenuContent);

export default React.forwardRef((props, ref) => <WithRef {...props} forwardedRef={ref} />);
